<template>
  <div>
    <!-- Enable Toggle -->
    <b-card class="mb-3" header="Order Routing Settings" no-body>
      <div class="card-body">
        <b-row>
          <b-col cols="12" class="mb-2">
            <b-form-checkbox
              v-model="config.useConnectOrderRouting"
              name="useConnectOrderRouting"
              switch
              size="lg"
            >
              Enable Connect Order Routing
            </b-form-checkbox>
            <small class="text-muted">When enabled, incoming orders will be automatically routed to a storefront (Ship From location) based on the rules below.</small>
          </b-col>
          <b-col cols="12" class="mt-3">
            <b-form-checkbox
              v-model="config.forceMergeShopifySplitOrders"
              name="forceMergeShopifySplitOrders"
              switch
            >
              Force Merge Shopify Split Orders
            </b-form-checkbox>
            <small class="text-muted">Affects only Shopify Direct integrations. If an incoming Shopify order has multiple fulfillment orders, attempt to merge them into one. If merging fails, the fulfillment orders will be placed on hold in Shopify instead.</small>
          </b-col>

          <b-col cols="12" class="mt-2" v-if="config.shopifyWebhookSusbcriptionId">
            <b-form-group label="Shopify Webhook Subscription ID:" description="Managed automatically by the system.">
              <b-form-input :value="config.shopifyWebhookSusbcriptionId" readonly />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Rules -->
    <b-card class="mb-3" no-body>
      <template v-slot:header>
        <div class="d-flex w-100">
          <span>Routing Rules</span>
          <b-button size="sm" variant="primary" @click="addRule" class="ml-auto">
            <i class="fa fa-plus mr-1"></i> Add Rule
          </b-button>
        </div>
      </template>

      <div v-if="!config.rules || config.rules.length === 0" class="card-body text-muted">
        No routing rules configured. Add a rule to get started.
      </div>

      <div v-for="(rule, ruleIdx) in config.rules" :key="ruleIdx" class="border-bottom">
        <div class="card-body pb-2">
          <b-row class="align-items-center mb-3">
            <b-col cols="12" md="6">
              <b-form-group label="Route to Storefront (Ship From):" class="mb-0">
                <b-form-select
                  v-model="rule.storefrontId"
                  :options="storefrontOptionsForRule(rule)"
                  @change="function(val) { onStorefrontSelected(rule, val); }"
                >
                  <template v-slot:first>
                    <b-form-select-option value="" disabled>-- Select Storefront --</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <small class="text-muted">{{ rule.conditions ? rule.conditions.length : 0 }} condition(s) — all must be met to apply this rule</small>
            </b-col>
            <b-col cols="12" md="2" class="text-right">
              <b-button size="sm" variant="outline-danger" @click="removeRule(ruleIdx)">
                <i class="fa fa-trash"></i> Remove
              </b-button>
            </b-col>
          </b-row>

          <!-- Conditions -->
          <div class="pl-3 border-left ml-2">
            <div
              v-for="(condition, condIdx) in rule.conditions"
              :key="condition.id"
              class="mb-3 p-2 rounded"
            >
              <b-row>

                <!-- IF / AND label (conditions are always combined with AND) -->
                <b-col cols="auto" class="d-flex align-items-center pb-1">
                  <span v-if="condIdx > 0" class="badge badge-secondary p-2" style="margin-bottom: 6px">AND</span>
                  <span v-else class="badge badge-secondary p-2" style="margin-bottom: 6px">IF</span>
                </b-col>

                <!-- Match Type -->
                <b-col cols="12" md="3">
                  <b-form-group class="mb-0">
                    <b-form-select
                      v-model="condition.type"
                      :options="ruleTypeOptions"
                      size="sm"
                      @change="onConditionTypeChange(rule, condition)"
                    />
                  </b-form-group>
                </b-col>

                <!-- Country filter (ByPostCode / ByRegion only) -->
                <b-col cols="12" md="2" v-if="condition.type === 'ByPostCode' || condition.type === 'ByRegion'">
                  <b-form-group label="For Country:" description="Only trigger for this country." class="mb-0">
                    <b-v-select
                      v-model="condition.filterForCountryCode"
                      :searchable="true"
                      :options="countryCodeList"
                      size="sm"
                    />
                  </b-form-group>
                </b-col>

                <!-- Country filter (ByZone → locked to US) -->
                <b-col cols="12" md="2" v-if="condition.type === 'ByZone'">
                  <b-form-group label="For Country:" description="Zone routing applies to US orders only." class="mb-0">
                    <b-form-input value="US" readonly size="sm" />
                  </b-form-group>
                </b-col>

                <!-- Group -->
                <!---<b-col cols="12" md="2">
                  <b-form-group label="Group:" description="Conditions sharing a group are evaluated together." class="mb-0">
                    <b-form-input
                      v-model="condition.group"
                      size="sm"
                      placeholder="DEFAULT"
                    />
                  </b-form-group>
                </b-col>-->

                <!-- Remove condition -->
                <b-col cols="12" md="1" class="d-flex align-items-end pb-1 ml-auto">
                  <b-button size="sm" variant="outline-danger" @click="removeCondition(rule, condIdx)" title="Remove condition">
                    <i class="fa fa-times"></i>
                  </b-button>
                </b-col>
              </b-row>

              <!-- Values row -->
              <b-row class="mt-2">
                <b-col cols="1">
                  <div class="border-left border-bottom" style="border-width: 3px !important; margin-left: 25px; display: block; height: 25px;">&nbsp;</div>
                </b-col>
                <b-col cols="11">
                  <!-- ByZone → priority input instead of a value list -->
                  <template v-if="condition.type === 'ByZone'">
                    <b-form-group label="Priority (1 = highest):" description="Breaks ties when warehouses return the same zone for a US order. Must be unique across all zone conditions." class="mb-2" style="max-width: 260px;">
                      <b-form-input
                        v-model.number="condition.zonePriority"
                        type="number"
                        min="1"
                        step="1"
                        size="sm"
                        @change="onZonePriorityChange(condition)"
                      />
                    </b-form-group>
                    <b-form-checkbox
                      v-model="condition.checkForAvailableInventory"
                      switch
                      size="sm"
                    >
                      Only route here if the location has enough inventory
                    </b-form-checkbox>
                    <small class="text-muted d-block">If enabled, this location is skipped when it can't fulfill the order. If no zone location has inventory, the order still routes to the highest-priority location.</small>
                  </template>
                  <template v-else>
                  <label class="mb-1" style="font-size: 0.8rem; font-weight: 600;">{{ getValuesLabel(condition.type) }}</label>

                  <!-- Add value input -->
                  <!-- ByCountryCode → custom country picker with flags + region groups -->
                 <div
                      v-if="condition.type === 'ByCountryCode'">
                   <country-code-select
                      :grouped-options="groupedCountryCodeList"
                      style="width: 400px;"
                      :exclude-values="condition.values || []"
                      @add="function(val) { addSingleValue(rule, condition, val); }"
                      @add-many="function(vals) { addManyValues(rule, condition, vals); }"
                      @remove-many="function(vals) { removeManyValues(condition, vals); }"
                    />
                 </div>
                  <!-- ByRegion + US filter → USA state select + Add button -->
                  <b-input-group v-else-if="condition.type === 'ByRegion' && condition.filterForCountryCode === 'US'" size="sm" style="max-width: 350px;">
                    <b-v-select
                      v-model="pendingValues[condition.id]"
                      :searchable="true"
                      :options="availableStateOptions(condition)"
                      placeholder="Add state..."
                      style="flex: 1;"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-primary"
                        @click="addValue(rule, condition)"
                        :disabled="!pendingValues[condition.id]"
                      >
                        Add
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <!-- ByPostCode or ByRegion (non-US) → plain input + Add button -->
                  <b-input-group v-else size="sm" style="max-width: 350px;">
                    <b-form-input
                      v-model="pendingValues[condition.id]"
                      placeholder="Add value..."
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-primary"
                        @click="addValue(rule, condition)"
                        :disabled="!pendingValues[condition.id]"
                      >
                        Add
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <!-- Values as badges, shown below the input -->
                  <div class="mt-2" v-if="condition.values && condition.values.length > 0">

                    <!-- ByCountryCode: grouped by region with flags -->
                    <template v-if="condition.type === 'ByCountryCode'">
                      <div
                        v-for="group in getCountriesByRegion(condition.values)"
                        :key="group.label"
                        class="mb-2"
                      >
                        <div class="mb-1" style="font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #6c757d;">
                          {{ group.label }}
                        </div>
                        <b-badge
                          v-for="country in group.items"
                          :key="country.value"
                          variant="light"
                          class="mr-1 mb-1 pl-1 pr-2 py-1 border d-inline-flex align-items-center"
                          style="font-size: 0.8rem; cursor: default;"
                        >
                          <img
                            :src="'https://flagcdn.com/16x12/' + country.value.toLowerCase() + '.png'"
                            :srcset="'https://flagcdn.com/32x24/' + country.value.toLowerCase() + '.png 2x'"
                            width="16"
                            height="12"
                            :alt="country.value"
                            class="mr-1"
                            style="border: 1px solid #ddd; flex-shrink: 0;"
                            @error="function(e) { e.target.style.display = 'none'; }"
                          />
                          {{ country.text }} ({{ country.value }})
                          <span
                            class="ml-1"
                            style="cursor: pointer;"
                            @click="removeValueByCode(condition, country.value)"
                          >&times;</span>
                        </b-badge>
                      </div>
                    </template>

                    <!-- All other types: flat badges -->
                    <template v-else>
                      <b-badge
                        v-for="(val, vIdx) in condition.values"
                        :key="vIdx"
                        variant="light"
                        class="mr-1 mb-1 pl-1 pr-2 py-1 border d-inline-flex align-items-center"
                        style="font-size: 0.85rem; cursor: default;"
                      >
                        <template v-if="condition.type === 'ByRegion' && condition.filterForCountryCode === 'US'">
                          <img
                            :src="'https://flagcdn.com/16x12/us-' + val.toLowerCase() + '.png'"
                            :srcset="'https://flagcdn.com/32x24/us-' + val.toLowerCase() + '.png 2x'"
                            width="16"
                            height="12"
                            :alt="val"
                            class="mr-1"
                            style="border: 1px solid #ddd; flex-shrink: 0;"
                            @error="function(e) { e.target.style.display = 'none'; }"
                          />
                          {{ getStateName(val) || val }} ({{ val }})
                        </template>
                        <template v-else>{{ val }}</template>
                        <span
                          class="ml-1"
                          style="cursor: pointer;"
                          @click="removeValue(condition, vIdx)"
                        >&times;</span>
                      </b-badge>
                    </template>

                  </div>
                  </template>
                </b-col>
              </b-row>
            </div>

            <b-button size="sm" variant="outline-secondary" class="mt-1 mb-2" @click="addCondition(rule)">
              <i class="fa fa-plus mr-1"></i> Add Condition
            </b-button>
          </div>
        </div>
      </div>
      <div class="p-3">
          <b-button size="lg" variant="primary" @click="addRule">
            <i class="fa fa-plus mr-1"></i> Add Rule
          </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import CountryCodeSelect from '../../../components/CountryCodeSelect.vue';

export default {
  name: 'StorefrontOrderRoutingConfig',
  components: { CountryCodeSelect },
  mixins: [myCommonDataMixin],

  props: {
    value: {
      type: Object,
      default: null
    },
    brandId: {
      type: String,
      default: ''
    }
  },

  data: function() {
    return {
      // keyed by condition.id → pending value string for that condition's add-input
      pendingValues: {}
    };
  },

  computed: {
    ...mapGetters({
      countryCodeList: 'getCountryCodeList',
      groupedCountryCodeList: 'getGroupedCountryCodeList',
      USAStateCodeList: 'getUSAStateCodeList'
    }),
    config: function() {
      if (!this.value) {
        return { useConnectOrderRouting: false, shopifyWebhookSusbcriptionId: null, rules: [] };
      }
      return this.value;
    },
    // True when any location has a ByZone (closest-warehouse-by-zone) condition. While active,
    // Region/State routing is not allowed on any location.
    anyRuleUsesZoneRouting: function() {
      return (this.config.rules || []).some(function(r) {
        return (r.conditions || []).some(function(c) { return c.type === 'ByZone'; });
      });
    },
    // Region routing is hidden whenever any location uses closest-warehouse-by-zone routing.
    ruleTypeOptions: function() {
      var opts = [
        { value: 'ByCountryCode', text: 'Country Code' },
        //{ value: 'ByPostCode', text: 'By Post Code' },
        { value: 'ByZone', text: 'Closest Warehouse by Zone (US)' }
      ];
      if (!this.anyRuleUsesZoneRouting) {
        opts.push({ value: 'ByRegion', text: 'Region' });
      }
      return opts;
    },
  },

  mounted: function() {
    if (!this.value) {
      this.$emit('input', {
        useConnectOrderRouting: false,
        shopifyWebhookSusbcriptionId: null,
        rules: []
      });
    }
  },

  methods: {
    storefrontOptionsForRule: function(rule) {
      var self = this;
      var usedIds = (self.config.rules || [])
        .filter(function(r) { return r !== rule && r.storefrontId; })
        .map(function(r) { return r.storefrontId; });
      return (self.myProjects || [])
        .filter(function(p) {
          return p.integrationStatus === 'Active' &&
            (!self.brandId || p.brandId === self.brandId) &&
            usedIds.indexOf(p.id) === -1;
        })
        .map(function(p) {
          return { value: p.id, text: p.name };
        });
    },

    ensureConfig: function() {
      if (!this.value) {
        this.$emit('input', {
          useConnectOrderRouting: false,
          shopifyWebhookSusbcriptionId: null,
          rules: []
        });
        return;
      }
      if (!this.config.rules) {
        this.$set(this.config, 'rules', []);
      }
    },

    addRule: function() {
      this.ensureConfig();
      this.config.rules.push({ storefrontId: '', conditions: [] });
    },

    // Whether the given rule has a ByZone (closest-warehouse-by-zone) condition.
    ruleHasByZone: function(rule) {
      return (rule.conditions || []).some(function(c) { return c.type === 'ByZone'; });
    },

    // Whether any rule has a Region/State condition configured.
    anyRuleUsesRegion: function() {
      return (this.config.rules || []).some(function(r) {
        return (r.conditions || []).some(function(c) { return c.type === 'ByRegion'; });
      });
    },

    // Lowest positive integer not yet used as a priority by any ByZone condition.
    nextAvailablePriority: function() {
      var used = [];
      (this.config.rules || []).forEach(function(r) {
        (r.conditions || []).forEach(function(c) {
          if (c.type === 'ByZone' && c.zonePriority) {
            used.push(parseInt(c.zonePriority, 10));
          }
        });
      });
      var p = 1;
      while (used.indexOf(p) !== -1) { p++; }
      return p;
    },

    onZonePriorityChange: function(condition) {
      var val = parseInt(condition.zonePriority, 10);
      if (isNaN(val) || val < 1) {
        this.$swal('Invalid Priority', 'Priority must be a whole number of 1 or greater (1 = highest priority).', 'error');
        this.$set(condition, 'zonePriority', this.nextAvailablePriority());
        return;
      }
      var duplicate = false;
      (this.config.rules || []).forEach(function(r) {
        (r.conditions || []).forEach(function(c) {
          if (c !== condition && c.type === 'ByZone' && parseInt(c.zonePriority, 10) === val) {
            duplicate = true;
          }
        });
      });
      if (duplicate) {
        this.$swal('Duplicate Priority', 'Another location already uses priority ' + val + '. Each zone condition must have a unique priority.', 'error');
        this.$set(condition, 'zonePriority', this.nextAvailablePriority());
        return;
      }
      this.$set(condition, 'zonePriority', val);
    },

    onStorefrontSelected: function(rule, newStorefrontId) {
      var duplicate = this.config.rules.some(function(r) {
        return r !== rule && r.storefrontId === newStorefrontId;
      });
      if (duplicate) {
        this.$swal('Duplicate Rule', 'A routing rule for this storefront already exists. Each storefront can only have one rule.', 'error');
        rule.storefrontId = '';
      }
    },

    removeRule: function(ruleIdx) {
      this.config.rules.splice(ruleIdx, 1);
    },

    addCondition: function(rule) {
      if (!rule.conditions) {
        this.$set(rule, 'conditions', []);
      }
      var id = this.generateId();
      rule.conditions.push({
        group: 'DEFAULT',
        id: id,
        logicOperator: 'AND',
        type: 'ByCountryCode',
        values: [],
        filterForCountryCode: null
      });
      this.$set(this.pendingValues, id, '');
    },

    removeCondition: function(rule, condIdx) {
      var id = rule.conditions[condIdx].id;
      rule.conditions.splice(condIdx, 1);
      this.$delete(this.pendingValues, id);
    },

    onConditionTypeChange: function(rule, condition) {
      if (condition.type === 'ByZone') {
        // Only one ByZone (US) condition makes sense per location.
        var hasOtherZone = (rule.conditions || []).some(function(c) {
          return c !== condition && c.type === 'ByZone';
        });
        if (hasOtherZone) {
          this.$swal('Not Allowed', 'This location already routes the US by Closest Warehouse (Zone). Only one zone condition is allowed per location.', 'error');
          condition.type = 'ByCountryCode';
          condition.filterForCountryCode = null;
          return;
        }
        // ByZone cannot coexist with Region/State routing anywhere in the config.
        if (this.anyRuleUsesRegion()) {
          this.$swal('Not Allowed', 'Closest Warehouse by Zone cannot be used while any location has Region / State routing configured. Remove all Region rules first.', 'error');
          condition.type = 'ByCountryCode';
          return;
        }
        this.$set(condition, 'values', []);
        this.$set(condition, 'filterForCountryCode', 'US');
        this.$set(condition, 'zonePriority', this.nextAvailablePriority());
        this.$set(condition, 'checkForAvailableInventory', false);
        this.$set(this.pendingValues, condition.id, '');
        return;
      }
      condition.values = [];
      if (condition.type === 'ByCountryCode') {
        condition.filterForCountryCode = null;
      }
      this.$set(condition, 'zonePriority', null);
      this.$set(condition, 'checkForAvailableInventory', false);
      this.$set(this.pendingValues, condition.id, '');
    },

    addSingleValue: function(rule, condition, val) {
      val = (val || '').trim().toUpperCase();
      if (!val) return;
      if (condition.type === 'ByCountryCode' && val === 'US' && this.ruleHasByZone(rule)) {
        this.$swal('Not Allowed', 'This location already routes the US by Closest Warehouse (Zone). Add US routing via the zone condition, not a country condition.', 'error');
        return;
      }
      if (!this._checkDuplicateBeforeAdd(rule, condition, [val])) return;
      if (!condition.values) this.$set(condition, 'values', []);
      condition.values.push(val);
    },

    addManyValues: function(rule, condition, vals) {
      var self = this;
      var blockUs = condition.type === 'ByCountryCode' && self.ruleHasByZone(rule);
      if (!condition.values) self.$set(condition, 'values', []);
      vals.forEach(function(val) {
        val = (val || '').trim().toUpperCase();
        if (!val) return;
        // US is routed by the zone condition on this location, so it can't be added as a country (silent skip for bulk adds).
        if (blockUs && val === 'US') return;
        if ((condition.values || []).indexOf(val) !== -1) return;
        // cross-rule dedup check (silent skip for bulk adds)
        var allOtherValues = [];
        (self.config.rules || []).forEach(function(r) {
          (r.conditions || []).forEach(function(c) {
            if (c.id !== condition.id && c.type === condition.type) {
              (c.values || []).forEach(function(v) { allOtherValues.push(v); });
            }
          });
        });
        if (allOtherValues.indexOf(val) !== -1) return;
        condition.values.push(val);
      });
    },

    _checkDuplicateBeforeAdd: function(rule, condition, vals) {
      var self = this;
      for (var i = 0; i < vals.length; i++) {
        var val = vals[i];
        if ((condition.values || []).indexOf(val) !== -1) {
          self.$swal('Duplicate Value', 'The value "' + val + '" is already in this condition.', 'error');
          return false;
        }
        var allOtherValues = [];
        (self.config.rules || []).forEach(function(r) {
          (r.conditions || []).forEach(function(c) {
            if (c.id !== condition.id && c.type === condition.type) {
              (c.values || []).forEach(function(v) { allOtherValues.push(v); });
            }
          });
        });
        if (allOtherValues.indexOf(val) !== -1) {
          self.$swal('Duplicate Value', 'The value "' + val + '" already exists in another condition of the same type.', 'error');
          return false;
        }
      }
      return true;
    },

    addValue: function(rule, condition) {
      var val = (this.pendingValues[condition.id] || '').trim().toUpperCase();
      if (!val) return;
      if (!this._checkDuplicateBeforeAdd(rule, condition, [val])) return;
      if (!condition.values) this.$set(condition, 'values', []);
      condition.values.push(val);
      this.$set(this.pendingValues, condition.id, '');
    },

    removeValue: function(condition, vIdx) {
      condition.values.splice(vIdx, 1);
    },

    removeValueByCode: function(condition, val) {
      var idx = (condition.values || []).indexOf(val);
      if (idx !== -1) condition.values.splice(idx, 1);
    },

    getCountriesByRegion: function(selectedValues) {
      var groups = [];
      var current = null;
      (this.groupedCountryCodeList || []).forEach(function(opt) {
        if (opt.disabled) {
          current = { label: opt.text.replace(/──\s*/g, '').replace(/\s*──/g, '').trim(), items: [] };
          groups.push(current);
        } else if (current && selectedValues.indexOf(opt.value) !== -1) {
          current.items.push({ value: opt.value, text: opt.text });
        }
      });
      return groups.filter(function(g) { return g.items.length > 0; });
    },

    removeManyValues: function(condition, vals) {
      if (!condition.values) return;
      condition.values = condition.values.filter(function(v) {
        return vals.indexOf(v) === -1;
      });
    },

    availableStateOptions: function(condition) {
      var existing = condition.values || [];
      return (this.USAStateCodeList || []).filter(function(opt) {
        return existing.indexOf(opt.value) === -1;
      });
    },

    getCountryName: function(code) {
      var opt = (this.countryCodeList || []).find(function(o) { return o.value === code; });
      return opt ? opt.text : code;
    },

    getStateName: function(code) {
      var opt = (this.USAStateCodeList || []).find(function(o) { return o.value === code; });
      return opt ? opt.text : null;
    },

    getValuesLabel: function(type) {
      if (type === 'ByCountryCode') return 'Is One of These Countries:';
      if (type === 'ByPostCode') return 'Matches Post / Zip Codes:';
      if (type === 'ByRegion') return 'Is One of These Regions / States:';
      return 'Values:';
    },

    generateId: function() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0;
        var v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
  }
};
</script>
